import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { ArrowUpDown, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Pack } from '@shared/schema';

// Extended pack type with additional fields
export interface PackWithStatus extends Pack {
  versionNumber: number;
  parentPackId?: number;
  isLocked: boolean;
  lockStatus: string;
  creationSource: string;
}

export interface PackColumnHandlers {
  PackLockStatusBadge: React.ComponentType<{ packId: number }>;
  PackLockToggleButton: React.ComponentType<{ pack: Pack }>;
  PackEditButton: React.ComponentType<{ pack: Pack; onEdit: (pack: Pack, mode: 'view' | 'edit') => void }>;
  onEdit: (pack: Pack, mode: 'view' | 'edit') => void;
  onDelete: (pack: Pack) => void;
}

export function createPackColumns(
  handlers: PackColumnHandlers
): ColumnDef<Pack>[] {
  const { PackLockStatusBadge, PackLockToggleButton, PackEditButton, onEdit, onDelete } = handlers;

  return [
    // Pack ID column
    {
      accessorKey: 'id',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Pack ID
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="font-mono text-sm text-muted-foreground">{row.original.id}</span>
      ),
    },
    // Pack Name column
    {
      accessorKey: 'name',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Pack Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const pack = row.original;
        const versionNumber = (pack as any).versionNumber || 1;
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium">{pack.name}</span>
            <span className="text-xs text-muted-foreground bg-blue-100 text-blue-800 px-2 py-1 rounded">
              v{versionNumber}
            </span>
          </div>
        );
      },
    },
    // Description column
    {
      accessorKey: 'description',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Description
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="text-sm">{row.original.description || "No description"}</span>
      ),
    },
    // Status column
    {
      accessorKey: 'isActive',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Status
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const isArchived = row.original.isActive === false;
        return isArchived ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Archived
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Active
          </span>
        );
      },
    },
    // Lock Status column
    {
      id: 'lockStatus',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Lock Status
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => <PackLockStatusBadge packId={row.original.id} />,
    },
    // Actions column
    {
      id: 'actions',
      header: () => <div className="text-left">Actions</div>,
      cell: ({ row }) => {
        const pack = row.original;
        return (
          <div className="flex space-x-2">
            <PackLockToggleButton pack={pack} />
            <PackEditButton pack={pack} onEdit={onEdit} />
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(pack)}
              className="px-2"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
      enableSorting: false,
    },
  ];
}








